import React, { useRef, useContext, useEffect, useState } from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import Truncate from 'common/Truncate';
import { TabContext } from 'components/Tabs';
import { HorizontalNavigationContext } from './HorizontalNavigation';
import Styled from './styles';

export interface HorizontalNavigationLabelProps extends React.ComponentPropsWithRef<'div'> {
  /** Indicates the current tab is selected or active tab. Label is also selected by the selected property in Tab. */
  selected?: boolean;
  /** Indicates the current tab is disabled. Label is also disabled by the disabled property in Tab. */
  disabled?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** The label contents. */
  children: string;
}

/** Displays HorizontalNavigation tab text.  All other props will be provided to the wrapper element.  */
const HorizontalNavigationLabel = React.forwardRef<HTMLElement, HorizontalNavigationLabelProps>((props: HorizontalNavigationLabelProps, forwardedRef) =>
  getContext(({ theme }) => {
    const { children, selected, disabled, theme: userTheme, ...otherProps } = props;
    const tabContext: any = useContext(TabContext);
    const innerRef = useRef(null);
    const ref = useCombinedRefs(forwardedRef, innerRef);

    const horizontalNavigationContext = useContext(HorizontalNavigationContext);
    const [resolvedLines, setResolvedLines] = useState(horizontalNavigationContext.oneLine ? 1 : 2);

    const tooltipProps = {
      disable: true,
      tooltip: '',
    };

    useEffect(() => {
      setResolvedLines(horizontalNavigationContext.oneLine ? 1 : 2);
    }, [horizontalNavigationContext.oneLine]);

    // callback to Tab component; it will display a tooltip if the text has been clamped
    const handleClampEnd = (clampedObj) => {
      tabContext.enableTooltip && tabContext.enableTooltip(clampedObj);
    };

    const truncateStyle = { wordWrap: 'break-word' };
    const resolvedTheme = _merge({}, theme.HORIZONTAL_NAVIGATION_TAB, userTheme.HORIZONTAL_NAVIGATION_TAB);
    const resolvedSelected = selected || tabContext.selected;
    const resolvedDisabled = disabled || tabContext.disabled;
    return (
      <Styled.HorizontalNavigationLabel
        theme={resolvedTheme.navigation}
        ref={ref}
        $isPressed={tabContext.isPressed}
        {...otherProps}
        selected={resolvedSelected}
        disabled={resolvedDisabled}
      >
        <Truncate
          lines={resolvedLines}
          delay={100}
          text={children}
          onClampEnd={handleClampEnd}
          tooltip={tooltipProps}
          style={truncateStyle}
        />
      </Styled.HorizontalNavigationLabel>
    );
  }));

HorizontalNavigationLabel.defaultProps = {
  selected: undefined,
  disabled: undefined,
  theme: { HORIZONTAL_NAVIGATION_TAB: {} },
};

HorizontalNavigationLabel.displayName = 'HorizontalNavigationLabel';

export { HorizontalNavigationLabel as default };
